import _ from 'underscore';

export class ChartGranularityAdapter {
  constructor({ i18n, gridName, lowestSupportedGranularity } = {}) {
    this.i18n = i18n;
    this.gridName = gridName;
    this.granularityMap = [{
      id: 0,
      value: 'InlineChart_Granularity_Hourly',
      enumValue: 'Hour',
      disabled: false,
      dateFormat: { datetime: 'short' },
    }, {
      id: 1,
      value: 'InlineChart_Granularity_Daily',
      enumValue: 'Day',
      disabled: false,
      dateFormat: { skeleton: 'yMEd' },
    }, {
      id: 2,
      value: 'InlineChart_Granularity_Weekly',
      enumValue: 'Week',
      disabled: false,
      dateFormat: { skeleton: 'yMMMd' },
    }, {
      id: 3,
      value: 'InlineChart_Granularity_Monthly',
      enumValue: 'Month',
      disabled: false,
      dateFormat: { skeleton: 'yMMM' },
    }, {
      id: 4,
      value: 'InlineChart_Granularity_Quarterly',
      enumValue: 'Quarter',
      disabled: false,
      dateFormat: { skeleton: 'yQQQ' },
    }];
    this.lowestSupportedGranularity = lowestSupportedGranularity;
  }

  /**
   * Gets localized list of granularities for the current daterange.
   * @returns {object} localized list of granularity items as {id, value}
  */
  getGranularitiesList = () => _.reduce(this.granularityMap, (results, item) => {
    if (!item.disabled) {
      results.push({
        id: item.id,
        value: this.i18n.getString(item.value),
      });
    }
    return results;
  }, []);

  /**
   * Gets localized granularity item corresponding to the id.
   * @param {number} itemId - Id corresponding to the granularity item
   * @returns {object} localized list of granularity items as {id, value}
   */
  getGranularityItem = (itemId) => {
    if (itemId === undefined) {
      return itemId;
    }

    const item = _.findWhere(this.granularityMap, { id: itemId });
    return {
      id: item.id,
      value: this.i18n.getString(item.value),
    };
  };

  /**
   * Reconcile preferred granularity with available granularities.
   * @param {number} preferredId - granularity Id preference of the user
   * @returns {number} granularity id of the selected granularity
   */
  reconcileSelectedGranularity = (preferredId) => {
    const item = _.findWhere(this.granularityMap, { id: preferredId });

    // If the preferred granularity is not available for the current daterange, we fallback
    // to the lowest granularity level that is supported.
    if (!item || item.disabled) {
      return this.getLowestAvailableGranularityId();
    }

    // User preference is available in current granularity menu.
    return preferredId;
  };

  /**
   * Gets localized granularity item corresponding to the id.
   * @param {number} itemId - Id corresponding to the granularity item
   * @returns {object} localized list of granularity items as {id, value}
   */
  getLowestAvailableGranularityId = () => {
    const item = _.findWhere(this.granularityMap, { disabled: false });

    if (!item) {
      // All the granularities may be disabled (Eg. if current selected range is Today and
      // POP is same day last year, then only available granularity for Today which is 'hourly'
      // is not supported for period older than 6months rolling window. So in this case all
      // granularities will be in disabled state.)
      return undefined;
    }

    return item.id;
  };

  /**
   * Get the granularity item corresponding to the enum value.
   * (Enum value is being stored in the preference store.)
   * @param {string} enumVal - granularity enum
   * @returns {object} granularity item
   */
  getGranularityIdFromEnum = (enumVal) => {
    const item = _.findWhere(this.granularityMap, { enumValue: enumVal });
    return item.id;
  };

  /**
   * Get the granularity enum corresponding to the id value.
   * @param {number} itemId - granularity item id
   * @returns {object} granularity item
   */
  getGranularityEnumFromId = (itemId) => {
    const item = _.findWhere(this.granularityMap, { id: itemId });
    return item && item.enumValue;
  };

  /**
   * Set the granularity disabled status.
   * @param {number} itemId - granularity item id
   * @param {boolean} isDisabled flag
   * @returns {undefined}
   */
  setIsGranularityDisabled = (itemId, isDisabled) => {
    _.extend(_.findWhere(this.granularityMap, { id: itemId }), { disabled: isDisabled });
  };

  /**
   * Gets LDML date format corresponding to the granularity.
   * @param {number} itemId - granularity item id
   * @returns {object} LDML format option of granularity
   */
  getGranularityDateFormat = (itemId) => {
    const item = _.findWhere(this.granularityMap, { id: itemId });
    return item.dateFormat;
  };

  calculateDiff = (dateRange) => {
    if (dateRange) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());

      return Math.round(timeDiff / (1000 * 3600 * 24));
    }
    return null;
  };

  calculateDisabled = (dateDiff, min, max) => dateDiff < min || dateDiff > max;

  updateAvailableGranularities = (dateRange) => {
    const mainDateDiff = this.calculateDiff(dateRange);
    const comparedDateDiff = this.calculateDiff(dateRange.compared);
    const now = new Date();
    // we only support hourly data for a sliding 6 months window
    const hourlyStartDate = new Date(now.setMonth(now.getMonth() - 6));
    // UP Smart Performance Grid doesn't support hourly per backend team
    let hourlyOptionDisabled = this.gridName === 'SmartPerformanceGrid' ||
      mainDateDiff > 0 ||
      (comparedDateDiff ? comparedDateDiff > 0 : false) ||
      !(dateRange.start > hourlyStartDate) ||
      !(dateRange.compared ? dateRange.compared.start > hourlyStartDate : true);

    if (this.lowestSupportedGranularity === 'Day') {
      hourlyOptionDisabled = true;
    }

    let dailyOptionDisabled = this.calculateDisabled(mainDateDiff, 1, 30) ||
      (comparedDateDiff ? this.calculateDisabled(comparedDateDiff, 1, 30) : false);

    if (this.lowestSupportedGranularity === 'Day') {
      dailyOptionDisabled = this.calculateDisabled(mainDateDiff, 0, 30) ||
        (comparedDateDiff ? this.calculateDisabled(comparedDateDiff, 0, 30) : false);
    }

    const weeklyOptionDisabled = this.calculateDisabled(mainDateDiff, 7, 210) ||
      (comparedDateDiff ? this.calculateDisabled(comparedDateDiff, 7, 210) : false);

    const monthlyOptionDisabled = this.calculateDisabled(mainDateDiff, 31, 900) ||
      (comparedDateDiff ? this.calculateDisabled(comparedDateDiff, 31, 900) : false);

    const quarterlyOptionDisabled = this.calculateDisabled(mainDateDiff, 90, Infinity) ||
      (comparedDateDiff ? this.calculateDisabled(comparedDateDiff, 90, Infinity) : false);

    this.setIsGranularityDisabled(0, hourlyOptionDisabled);
    this.setIsGranularityDisabled(1, dailyOptionDisabled);
    this.setIsGranularityDisabled(2, weeklyOptionDisabled);
    this.setIsGranularityDisabled(3, monthlyOptionDisabled);
    this.setIsGranularityDisabled(4, quarterlyOptionDisabled);
  };
}
